import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Form, Grid, Icon, Message, Divider, Radio, Segment } from 'semantic-ui-react';
import { compose } from 'redux';
import connect from 'react-redux/es/connect/connect';
import { accessRequestsActions } from '../behavior/accessRequests';
import { Loading, InputWrapper, AdvancedFieldError } from 'react-lib/dist/components-es';
import { Formik } from 'formik';
import GridColumnInputWrapper from '../components/common/GridColumnInputWrapper';
import ButtonWrapper from '../components/common/ButtonWrapper';
import CommonSelectInputWrapper from '../components/common/CommonSelectInputWrapper';
import { getOptions } from '../utils';
import getAccessRequestFormSchema from '../formSchemas/AccessRequestFormSchema';
import { useSelector } from 'react-redux';
import useUser from '@behavior/init/getUserV2';

const AccessRequestPage = ({
  t,
  initialData,
  isLoading,
  requestStatus,
  getInitialData,
  newAccessRequest,
}) => {
  const { user } = useUser();
  useEffect(() => {
    getInitialData(); // TODO : RRA-1068 : What is the reason behind useEffect here, should it be run only once?
  }, []);

  useEffect(() => {
    if (initialData) {
      setRoleList(getOptions(initialData.roleList, null, ''));
      prepareDepartmentAndExperimentList();
      if (initialData.departmentList.includes(user?.actualDepartment))
        setInitialDepartment(user?.actualDepartment);
    }
  }, [initialData]);

  const [roleList, setRoleList] = useState();
  const [departmentAndExperimentList, setDepartmentAndExperimentList] = useState();
  const [initialDepartment, setInitialDepartment] = useState();
  const [selectedRole, setSelectedRole] = useState();

  const wasRequestSuccessful = useSelector(state => state?.accessRequests?.requestStatus) === 200;
  const isRequestAccepted = useSelector(state => state?.accessRequests?.isAccepted);
  const accessRequestDto = useSelector(state => state?.accessRequests?.accessRequestDto);

  function prepareDepartmentAndExperimentList() {
    const depOptions = getOptions(initialData.departmentList, null, '');
    const expOptions = getOptions(initialData.experimentList, null, '');
    const depExpDivider = {
      content: <Divider style={{ margin: 0 }} />,
      disabled: true,
      key: 'depExpDivider',
    };
    setDepartmentAndExperimentList(depOptions.concat(depExpDivider).concat(expOptions));
  }

  function manipulateValuesAndSubmit(values) {
    if (initialData?.experimentList.includes(values.departmentOrExperiment)) {
      values.experiment = values.departmentOrExperiment;
      values.department = null; // Workaround - component remembers default/previous state of dep/exp selection and it causes issues in backend
    } else {
      values.department = values.departmentOrExperiment;
      values.experiment = null; // Workaround - component remembers default/previous state of dep/exp selection and it causes issues in backend
    }
    newAccessRequest(values);
  }

  function roleSelectionRadioGroup(formikProps) {
    return (
      <div className="roleGroup">
        <label>{t('access.request.page.input.role.label')}</label>
        {!selectedRole && (
          <AdvancedFieldError messages={t('access.request.page.validation.missing.role')} />
        )}
        <Segment>
          <Form.Group>
            {roleList.map(({ key: roleValue }) => anyRoleRadioButton(roleValue, formikProps))}
          </Form.Group>
        </Segment>
      </div>
    );
  }

  function anyRoleRadioButton(roleValue, formikProps) {
    return (
      <Form.Field
        error={!selectedRole}
        name="roleRadioGroup"
        control={Radio}
        value={roleValue}
        label={t(roleValue)}
        checked={selectedRole === roleValue}
        onChange={(e, { value }) => {
          setSelectedRole(value);
          formikProps.setFieldValue('role', value, true);
        }}
      />
    );
  }

  function roleInformationContent() {
    return (
      <Message className="roleGroup">
        <Message.Header>Ceres Roles</Message.Header>
        <p>
          <b>{`${t('ROLE_EDT')}:`}</b>
          &emsp;{`${t('access.request.page.information.popup.editor')}`}
        </p>
        <p>
          <b>{`${t('ROLE_VIW')}:`}</b>
          &emsp;{`${t('access.request.page.information.popup.viewer')}`}
        </p>
      </Message>
    );
  }

  function departmentOrExperimentSelectionDropdown(formikProps) {
    return (
      <GridColumnInputWrapper
        {...formikProps}
        label="access.request.page.input.department.or.experiment.label"
        name="departmentOrExperiment"
        value={formikProps.values.departmentOrExperiment}
        options={departmentAndExperimentList}
        specialRequiredCondition
        render={props => <CommonSelectInputWrapper {...props} {...formikProps} />}
      />
    );
  }

  function requestStatusMessagePanel() {
    const initialMessagePanel = () => (
      <Message color={'yellow'}>
        <Icon name="warning sign" />
        {t('access.request.page.success.new.access.request.info')}
      </Message>
    );

    const responseMessagePanel = () => {
      const { role: requestedRole, departmentOrExperiment: requestedDepartmentOrExperiment } =
        accessRequestDto;
      return (
        <Message color={wasRequestSuccessful && isRequestAccepted ? 'green' : 'red'}>
          {wasRequestSuccessful &&
            isRequestAccepted &&
            t(`message.title.success.new.access.request`)}
          {wasRequestSuccessful &&
            !isRequestAccepted &&
            t('message.title.error.new.access.request.is.duplicate', {
              role: t(requestedRole),
              departmentOrExperiment: requestedDepartmentOrExperiment,
            })}
          {!wasRequestSuccessful && t(`message.title.error.new.access.request`)}
        </Message>
      );
    };

    return (
      <Grid.Row>
        <Grid.Column computer={6} tablet={10}>
          {initialMessagePanel()}
          {roleInformationContent()}
          {requestStatus && responseMessagePanel()}
        </Grid.Column>
      </Grid.Row>
    );
  }

  function accessRequestForm(formikProps) {
    return (
      <Form>
        <Grid.Column>{roleSelectionRadioGroup(formikProps)}</Grid.Column>
        {departmentOrExperimentSelectionDropdown(formikProps)}
        <GridColumnInputWrapper
          label="access.request.page.input.comments.label"
          name="comments"
          render={props => <InputWrapper {...props} {...formikProps} type="textArea" />}
        />
        <Grid.Column width={10} />
        <Grid.Column width={6} style={{ marginTop: '15px' }}>
          <ButtonWrapper
            className="mini right floated"
            key="new-access-request-button"
            type="button"
            label="access.request.page.submit.button"
            onClick={() => formikProps.submitForm()}
            primary
          />
        </Grid.Column>
      </Form>
    );
  }

  function requestFormProcessor() {
    return (
      <Grid.Row>
        <Grid.Column computer={6} tablet={10}>
          <Formik
            validationSchema={getAccessRequestFormSchema()}
            initialValues={{ role: selectedRole, departmentOrExperiment: initialDepartment }}
            onSubmit={values => manipulateValuesAndSubmit(values)}
            render={formikProps => accessRequestForm(formikProps)}
          />
        </Grid.Column>
      </Grid.Row>
    );
  }

  function requestFormHeader() {
    return (
      <Grid.Row>
        <h3>{t('menu.access.request')}</h3>
      </Grid.Row>
    );
  }

  const shouldRenderRequestFormProcessor =
    !wasRequestSuccessful && initialData && roleList && departmentAndExperimentList;

  return (
    <Grid stackable className="centered withTopMargin">
      <Loading isLoading={isLoading} />
      {requestFormHeader()}
      {requestStatusMessagePanel()}
      {shouldRenderRequestFormProcessor && requestFormProcessor()}
    </Grid>
  );
};

const mapStateToProps = state => ({
  ...state.accessRequests,
});

const mapDispatchToProps = dispatch => ({
  getInitialData: () => dispatch(accessRequestsActions.getInitialData()),
  newAccessRequest: accessRequestDto =>
    dispatch(accessRequestsActions.newAccessRequest(accessRequestDto)),
});

export default React.memo(
  compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(AccessRequestPage)
);
